import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from './helpers';
import { UserPlus, Edit2, Save, XCircle, Mail, Check, Copy } from 'lucide-react';

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newUser, setNewUser] = useState({ email: '', firstName: '', lastName: '', role: 'user' });
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/users`);
      setUsers(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const CRM_URL = 'https://crm.cagan.com';

  const buildMailtoLink = (email, firstName) => {
    const subject = encodeURIComponent("You've been invited to Cagan CRM");
    const body = encodeURIComponent(
      `Hi ${firstName},\n\nYou've been added to the Cagan CRM system. To access it, go to:\n${CRM_URL}\n\nClick 'Sign in with Microsoft' and use your Cagan email to log in. No password setup needed — it uses your existing Microsoft 365 account.\n\nIf you have any questions, let me know.`
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(CRM_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/users`, newUser);
      setCreatedUser({ email: newUser.email, firstName: newUser.firstName });
      setNewUser({ email: '', firstName: '', lastName: '', role: 'user' });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.UserId);
    setEditData({
      firstName: user.FirstName,
      lastName: user.LastName,
      role: user.Role,
      isActive: user.IsActive,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (userId) => {
    try {
      await axios.put(`${API_BASE}/users/${userId}`, editData);
      setEditingId(null);
      setEditData({});
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="crm-loading">
        <div className="crm-loading__container">
          <div className="crm-loading__spinner" />
          <p className="crm-loading__text">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-section">
      <div className="crm-section__header">
        <h2 className="crm-section__title">Manage Users</h2>
        <button className="crm-btn crm-btn--primary" onClick={() => setShowAddForm(!showAddForm)}>
          <UserPlus size={16} />
          Add User
        </button>
      </div>

      {error && (
        <div className="crm-error" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>
            <XCircle size={16} />
          </button>
        </div>
      )}

      {createdUser && (
        <div className="crm-card" style={{ padding: '1.25rem', marginBottom: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Check size={18} style={{ color: '#16a34a' }} />
            <strong style={{ color: '#15803d' }}>User added successfully</strong>
            <button
              onClick={() => setCreatedUser(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
            >
              <XCircle size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a
              href={buildMailtoLink(createdUser.email, createdUser.firstName)}
              className="crm-btn crm-btn--primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <Mail size={16} />
              Send Invite to {createdUser.email}
            </a>
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              CRM link: <code style={{ background: '#e5e7eb', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{CRM_URL}</code>
              <button
                onClick={handleCopyUrl}
                style={{ marginLeft: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', verticalAlign: 'middle' }}
                title="Copy URL"
              >
                {copied ? <Check size={14} style={{ color: '#16a34a' }} /> : <Copy size={14} />}
              </button>
            </span>
          </div>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAdd} className="crm-card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Add New User</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="crm-form-group">
              <label className="crm-form-label">Email *</label>
              <input
                type="email"
                className="crm-form-input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">First Name *</label>
              <input
                type="text"
                className="crm-form-input"
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">Last Name *</label>
              <input
                type="text"
                className="crm-form-input"
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">Role</label>
              <select
                className="crm-form-input"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="crm-btn crm-btn--primary">Create User</button>
            <button type="button" className="crm-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="crm-card" style={{ overflow: 'auto' }}>
        <table className="crm-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Active</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.UserId}>
                <td>{user.Email}</td>
                <td>
                  {editingId === user.UserId ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        className="crm-form-input"
                        value={editData.firstName}
                        onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                        style={{ width: '100px' }}
                      />
                      <input
                        type="text"
                        className="crm-form-input"
                        value={editData.lastName}
                        onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                        style={{ width: '100px' }}
                      />
                    </div>
                  ) : (
                    `${user.FirstName} ${user.LastName}`
                  )}
                </td>
                <td>
                  {editingId === user.UserId ? (
                    <select
                      className="crm-form-input"
                      value={editData.role}
                      onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span className={`crm-badge ${user.Role === 'admin' ? 'crm-badge--primary' : 'crm-badge--default'}`}>
                      {user.Role}
                    </span>
                  )}
                </td>
                <td>
                  {editingId === user.UserId ? (
                    <select
                      className="crm-form-input"
                      value={editData.isActive ? '1' : '0'}
                      onChange={(e) => setEditData({ ...editData, isActive: e.target.value === '1' })}
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  ) : (
                    <span className={`crm-badge ${user.IsActive ? 'crm-badge--success' : 'crm-badge--danger'}`}>
                      {user.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td>{user.LastLoginDate ? new Date(user.LastLoginDate).toLocaleDateString() : 'Never'}</td>
                <td>
                  {editingId === user.UserId ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="crm-btn crm-btn--small crm-btn--primary" onClick={() => saveEdit(user.UserId)}>
                        <Save size={14} /> Save
                      </button>
                      <button className="crm-btn crm-btn--small" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button className="crm-btn crm-btn--small" onClick={() => startEdit(user)}>
                      <Edit2 size={14} /> Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
